import { m } from '@pricedrop/i18n';
import { createFileRoute } from '@tanstack/react-router';
import Bell from 'lucide-react/dist/esm/icons/bell';
import Radio from 'lucide-react/dist/esm/icons/radio';
import Star from 'lucide-react/dist/esm/icons/star';
import UserCircle2 from 'lucide-react/dist/esm/icons/user-circle-2';
import { parseAsString, useQueryState } from 'nuqs';
import * as React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { buildMeta } from '@/lib/seo';
import { DeliveryTab } from './-components/delivery-tab';
import { NotificationsTab } from './-components/notifications-tab';
import { ProfileTab } from './-components/profile-tab';
import { ReviewsTab } from './-components/reviews-tab';

export const Route = createFileRoute('/_authed/account/')({
  head: () => ({
    meta: buildMeta({
      title: 'My Account - Pricedrop',
      description: '',
      canonicalUrl: '/account',
      noindex: true,
    }),
  }),
  component: AccountPage,
});

const TAB_VALUES = ['profile', 'alerts', 'delivery', 'reviews'] as const;
type TabValue = (typeof TAB_VALUES)[number];

function AccountPage() {
  const [tab, setTab] = useQueryState('tab', parseAsString.withDefault('profile'));
  const activeTab = TAB_VALUES.includes(tab as TabValue) ? (tab as TabValue) : 'profile';

  // Back-compat redirect: ?tab=notifications → ?tab=alerts
  React.useEffect(() => {
    if (tab === 'notifications') {
      setTab('alerts', { history: 'replace' });
    }
  }, [tab, setTab]);

  return (
    <div className="container mx-auto px-4">
      <div className="space-y-4 py-6">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold tracking-tight">{m.account_title()}</h2>
        </div>

        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="profile">
              <UserCircle2 className="size-4" />
              <span className="ms-1.5">{m.account_tabs_profile()}</span>
            </TabsTrigger>
            <TabsTrigger value="alerts">
              <Bell className="size-4" />
              <span className="ms-1.5">{m.account_tabs_alerts()}</span>
            </TabsTrigger>
            <TabsTrigger value="delivery">
              <Radio className="size-4" />
              <span className="ms-1.5">{m.account_tabs_delivery()}</span>
            </TabsTrigger>
            <TabsTrigger value="reviews">
              <Star className="size-4" />
              <span className="ms-1.5">{m.account_tabs_reviews()}</span>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="profile">
            <ProfileTab />
          </TabsContent>
          <TabsContent value="alerts">
            <NotificationsTab />
          </TabsContent>
          <TabsContent value="delivery">
            <DeliveryTab />
          </TabsContent>
          <TabsContent value="reviews">
            <ReviewsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
